import React, { useState } from "react";
import {
  TextField,
  CircularProgress,
  Container,
  Box,
  Card,
  IconButton,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { resetPassword } from "../../service/authService";
import AppButton from "../../components/AppButton";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";

const Confirm = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const email = localStorage.getItem("email")?.trim();
  const otp = localStorage.getItem("otp")?.trim();

  const toggleNewPassword = () => setShowNewPassword(!showNewPassword);
  const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // Prevent space in password fields
  const preventSpace = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .matches(/[A-Z]/, "Must contain at least one uppercase letter")
        .matches(/\d/, "Must contain at least one number")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (
        !email ||
        !/\S+@\S+\.\S+/.test(email) ||
        !otp ||
        !/^\d{6}$/.test(otp)
      ) {
        showSnackbar(
          "Missing or invalid email/OTP. Please restart the process.",
          "error"
        );
        setSubmitting(false);
        return;
      }

      try {
        await resetPassword({
          email,
          otp,
          password: values.password,
          password_confirmation: values.confirmPassword,
        });
        showSnackbar("Password updated successfully", "success");
        resetForm();
        setTimeout(() => navigate("/"), 1500);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Something went wrong.";
        const errorDetails = error?.response?.data?.errors;

        showSnackbar(errorMessage, "error");
        if (errorDetails) {
          Object.entries(errorDetails).forEach(([field, messages]) => {
            messages.forEach((msg) =>
              showSnackbar(`${field}: ${msg}`, "error")
            );
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          borderRadius: 3,
          boxSizing: "border-box",
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          align="center"
          fontWeight="bold"
          gutterBottom
        >
          Set New Password
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit}>
          <Box mb={2}>
            <Typography
              sx={{
                mb: 1,
                fontWeight: "bold",
                color: Color.label.main,
                fontSize: "13px",
              }}
            >
              New Password*
            </Typography>
            <TextField
              fullWidth
              type={showNewPassword ? "text" : "password"}
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onKeyDown={preventSpace} // ← Prevents space key
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              placeholder="Enter new password"
              variant="outlined"
              InputLabelProps={{ shrink: false }}
              sx={{
                "& .MuiInputLabel-root": { display: "none" },
                mb: 0,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleNewPassword} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box mb={2}>
            <Typography
              sx={{
                mb: 1,
                fontWeight: "bold",
                color: Color.label.main,
                fontSize: "13px",
              }}
            >
              Confirm Password*
            </Typography>
            <TextField
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onKeyDown={preventSpace} // ← Prevents space key
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
              placeholder="Confirm new password"
              variant="outlined"
              InputLabelProps={{ shrink: false }}
              sx={{
                "& .MuiInputLabel-root": { display: "none" },
                mb: 0,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleConfirmPassword} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <AppButton
            type="submit"
            fullWidth
            color="primary"
            disabled={formik.isSubmitting}
            sx={{ mt: 3 }}
          >
            {formik.isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Update Password"
            )}
          </AppButton>

          <Button
            fullWidth
            onClick={handleCancel}
            sx={{
              mt: 1,
              background: "none",
              color: Color.label.main,
              border: "1px solid",
              borderColor: Color.secondary.main,
            }}
          >
            Back to Login
          </Button>
        </Box>
      </Card>
    </Container>
  );
};

export default Confirm;